import { useEffect, useId, useMemo, useRef, useState } from "react";
import type {
  FieldErrors,
  FieldValues,
  Path,
  PathValue,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { CheckCircle2, ChevronDown } from "lucide-react";
import { Spinner } from "@/components/ui";
import { useTranslation } from "react-i18next";
import { AddressAutocompleteInput } from "./AddressAutocompleteInput";
import { FieldError, fieldErrorId, Input } from "@/components/ui";
import { cn } from "@/lib/utils";
import {
  formatAddressFromFieldValues,
  type AddressDetailFieldNames,
} from "@/lib/addressSuggestion";
import {
  addressPartsValidationKey,
  getMissingAddressFields,
  isAddressComplete,
  partsFromSuggestion,
  type AddressPartField,
  type AddressPartsValue,
} from "@/lib/addressParts";
import { geocodingService } from "@/services/geocodingService";
import type { AddressSuggestion, AddressValidationState } from "@/types";

const VALIDATION_DEBOUNCE_MS = 450;

interface AddressSearchLabels {
  search: string;
  searchPlaceholder: string;
  editDetails: string;
  street?: string;
  streetPlaceholder?: string;
  city: string;
  cityPlaceholder?: string;
  postalCode: string;
  postalPlaceholder?: string;
  country: string;
  countryPlaceholder?: string;
  province?: string;
  provincePlaceholder?: string;
}

export type AddressDetailFieldPaths<TFieldValues extends FieldValues> = {
  street: Path<TFieldValues>;
  city: Path<TFieldValues>;
  postalCode: Path<TFieldValues>;
  province: Path<TFieldValues>;
  country: Path<TFieldValues>;
};

export interface AddressSearchWithDetailsProps<
  TFieldValues extends FieldValues,
> {
  fields: AddressDetailFieldPaths<TFieldValues>;
  register: UseFormRegister<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  watch: UseFormWatch<TFieldValues>;
  errors?: FieldErrors<TFieldValues>;
  labels: AddressSearchLabels;
  syncKey?: string;
  enableValidation?: boolean;
  /** Mark the detail fields (street, postal code, city, province, country) as required. */
  requiredDetails?: boolean;
}

function hasDetailErrors<TFieldValues extends FieldValues>(
  errors: FieldErrors<TFieldValues> | undefined,
  fields: Path<TFieldValues>[],
): boolean {
  if (!errors) return false;
  return fields.some((f) => Boolean(fieldErrorMessage(errors, f)));
}

function fieldErrorMessage<TFieldValues extends FieldValues>(
  errors: FieldErrors<TFieldValues> | undefined,
  name: Path<TFieldValues>,
): string | undefined {
  const message = errors?.[name]?.message;
  return typeof message === "string" ? message : undefined;
}

function readStringField<TFieldValues extends FieldValues>(
  watch: UseFormWatch<TFieldValues>,
  name: Path<TFieldValues>,
): string {
  const value = watch(name);
  return typeof value === "string" ? value : "";
}

function getAddressParts<TFieldValues extends FieldValues>(
  fields: AddressDetailFieldPaths<TFieldValues>,
  watch: UseFormWatch<TFieldValues>,
): AddressPartsValue {
  return {
    address: readStringField(watch, fields.street),
    city: readStringField(watch, fields.city),
    postal_code: readStringField(watch, fields.postalCode),
    country: readStringField(watch, fields.country),
  };
}

function toFieldNames<TFieldValues extends FieldValues>(
  fields: AddressDetailFieldPaths<TFieldValues>,
): AddressDetailFieldNames {
  return {
    street: fields.street,
    city: fields.city,
    postalCode: fields.postalCode,
    province: fields.province,
    country: fields.country,
  };
}

function setStringField<TFieldValues extends FieldValues>(
  setValue: UseFormSetValue<TFieldValues>,
  name: Path<TFieldValues>,
  value: string,
): void {
  setValue(name, value as PathValue<TFieldValues, typeof name>, {
    shouldDirty: true,
  });
}

export function AddressSearchWithDetails<TFieldValues extends FieldValues>({
  fields,
  register,
  setValue,
  watch,
  errors,
  labels,
  syncKey,
  enableValidation = true,
  requiredDetails = false,
}: AddressSearchWithDetailsProps<TFieldValues>) {
  const { t } = useTranslation("common");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [searchDisplay, setSearchDisplay] = useState("");
  const [highlightedFields, setHighlightedFields] = useState<AddressPartField[]>(
    [],
  );
  const [validation, setValidation] = useState<AddressValidationState>({
    status: "idle",
  });

  const validationRequestId = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastValidatedKeyRef = useRef<string | null>(null);

  const parts = getAddressParts(fields, watch);
  const { address, city, postal_code: postalCode, country } = parts;
  const previousAddressRef = useRef(address);
  const wasDetailsOpenRef = useRef(detailsOpen);

  const fieldNames = toFieldNames(fields);
  const fieldValues = {
    [fieldNames.street]: address,
    [fieldNames.city]: city,
    [fieldNames.postalCode]: postalCode,
    [fieldNames.country]: country,
  };

  const formattedFromParts = formatAddressFromFieldValues(fieldNames, fieldValues);

  const validationKey = useMemo(
    () => addressPartsValidationKey(parts),
    [address, city, postalCode, country],
  );

  useEffect(() => {
    setSearchDisplay(formattedFromParts);
    setDetailsOpen(false);
    setHighlightedFields([]);
    lastValidatedKeyRef.current = null;
    setValidation({ status: "idle" });
  }, [syncKey]); // eslint-disable-line react-hooks/exhaustive-deps -- external reset only

  useEffect(() => {
    const wasDetailsOpen = wasDetailsOpenRef.current;
    const previousAddress = previousAddressRef.current;

    wasDetailsOpenRef.current = detailsOpen;
    previousAddressRef.current = address;

    if (!detailsOpen || !wasDetailsOpen || address === previousAddress) return;
    setSearchDisplay(address);
  }, [detailsOpen, address]);

  useEffect(() => {
    if (!enableValidation) return;

    if (lastValidatedKeyRef.current === validationKey) {
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const currentParts = getAddressParts(fields, watch);
      const key = addressPartsValidationKey(currentParts);

      if (lastValidatedKeyRef.current === key) {
        return;
      }

      if (!isAddressComplete(currentParts)) {
        lastValidatedKeyRef.current = key;
        setValidation({ status: "incomplete" });
        return;
      }

      lastValidatedKeyRef.current = key;
      const requestId = ++validationRequestId.current;
      setValidation({ status: "checking" });

      void geocodingService
        .validateAddress(currentParts)
        .then((result) => {
          if (requestId !== validationRequestId.current) return;
          if (addressPartsValidationKey(getAddressParts(fields, watch)) !== key) {
            return;
          }
          if (result.valid) {
            setValidation({ status: "valid" });
          } else {
            setValidation({
              status: "invalid",
              reason: result.reason,
            });
          }
        })
        .catch(() => {
          if (requestId !== validationRequestId.current) return;
          if (addressPartsValidationKey(getAddressParts(fields, watch)) !== key) {
            return;
          }
          setValidation({ status: "invalid", reason: "request_failed" });
        });
    }, VALIDATION_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [validationKey, enableValidation, fields]); // eslint-disable-line react-hooks/exhaustive-deps -- watch read inside debounce

  const handleSelect = async (suggestion: AddressSuggestion, sessionToken: string) => {
    let resolved: typeof suggestion;
    try {
      resolved = await geocodingService.retrieve(suggestion.id, sessionToken);
    } catch {
      resolved = suggestion;
    }

    setSearchDisplay(resolved.label);
    const nextParts = partsFromSuggestion(resolved);
    setStringField(setValue, fields.street, resolved.address ?? resolved.label);
    setStringField(setValue, fields.city, resolved.city ?? "");
    setStringField(setValue, fields.postalCode, resolved.postal_code ?? "");
    setStringField(setValue, fields.province, resolved.province ?? "");
    setStringField(setValue, fields.country, resolved.country ?? "");

    const missing = getMissingAddressFields(nextParts);
    if (missing.length > 0) {
      setDetailsOpen(true);
      setHighlightedFields(missing);
    } else {
      setDetailsOpen(false);
      setHighlightedFields([]);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchDisplay(value);
    setStringField(setValue, fields.street, value);
  };

  const fieldHighlightError = (field: AddressPartField): string | undefined =>
    highlightedFields.includes(field)
      ? t("addressSearch.fieldRequired")
      : undefined;

  const detailFieldPaths: Path<TFieldValues>[] = [
    fields.street,
    fields.city,
    fields.postalCode,
    fields.province,
    fields.country,
  ];

  const detailError = hasDetailErrors(errors, detailFieldPaths);

  const searchFieldError = fieldErrorMessage(errors, fields.street);

  const validationStatusId = useId();
  const validationInvalid = enableValidation && validation.status === "invalid";
  const validationErrorId = fieldErrorId(validationStatusId);

  return (
    <div className="space-y-3">
      <AddressAutocompleteInput
        label={labels.search}
        placeholder={labels.searchPlaceholder}
        value={searchDisplay}
        onChange={handleSearchChange}
        onSelect={handleSelect}
        error={
          searchFieldError ??
          (detailError && !detailsOpen
            ? t("addressSearch.detailFieldsError")
            : undefined)
        }
        invalid={validationInvalid && !searchFieldError && !detailError}
        describedBy={validationInvalid ? validationErrorId : undefined}
      />

      <button
        type="button"
        className="flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
        onClick={() => setDetailsOpen((open) => !open)}
        aria-expanded={detailsOpen}
      >
        <ChevronDown
          className={cn(
            "h-4 w-4 transition-transform",
            detailsOpen && "rotate-180",
          )}
        />
        {labels.editDetails}
      </button>

      {detailsOpen ? (
        <div className="grid grid-cols-1 gap-4 rounded-xl border border-surface-200 bg-surface-50 p-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Input
              {...register(fields.street)}
              label={labels.street ?? labels.search}
              required={requiredDetails}
              placeholder={labels.streetPlaceholder}
              error={
                fieldErrorMessage(errors, fields.street) ??
                fieldHighlightError("address")
              }
            />
          </div>
          <Input
            {...register(fields.postalCode)}
            label={labels.postalCode}
            required={requiredDetails}
            placeholder={labels.postalPlaceholder}
            error={fieldErrorMessage(errors, fields.postalCode)}
          />
          <Input
            {...register(fields.city)}
            label={labels.city}
            required={requiredDetails}
            placeholder={labels.cityPlaceholder}
            error={fieldErrorMessage(errors, fields.city)}
          />
          {labels.province ? (
            <Input
              {...register(fields.province)}
              label={labels.province}
              required={requiredDetails}
              placeholder={labels.provincePlaceholder}
              error={fieldErrorMessage(errors, fields.province)}
            />
          ) : null}
          <div className={labels.province ? "" : "md:col-span-2"}>
            <Input
              {...register(fields.country)}
              label={labels.country}
              required={requiredDetails}
              placeholder={labels.countryPlaceholder}
              error={fieldErrorMessage(errors, fields.country)}
            />
          </div>
        </div>
      ) : null}

      {enableValidation ? (
        <div id={validationStatusId} className="text-sm" aria-live="polite">
          {validation.status === "checking" ? (
            <span className="inline-flex items-center gap-2 text-surface-600">
              <Spinner size="sm" loadingLabel={t("loading")} />
              {t("addressValidation.checking")}
            </span>
          ) : null}
          {validation.status === "valid" ? (
            <span className="inline-flex items-center gap-2 font-medium text-success-700">
              <CheckCircle2 className="h-4 w-4" />
              {t("addressValidation.valid")}
            </span>
          ) : null}
          {validation.status === "invalid" ? (
            <FieldError id={validationErrorId}>
              {t("addressValidation.invalid")}
            </FieldError>
          ) : null}
          {validation.status === "incomplete" && !isAddressComplete(parts) ? (
            <p className="text-surface-500">{t("addressValidation.incomplete")}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
